import React, { useState, useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import '../styles/Education.css';
import Squares from './Squares';

const Education = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  const cardsRef = useRef([]);
  // Remove unused waveRef

  useEffect(() => {
    let observer = null;
    if (sectionRef.current) {
      observer = new window.IntersectionObserver(
        ([entry]) => setIsVisible(entry.isIntersecting),
        { threshold: 0.1 }
      );
      observer.observe(sectionRef.current);
    }
    return () => {
      if (observer) observer.disconnect();
    };
  }, []);

  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger);
    if (!isVisible || !sectionRef.current) return;

    const cards = cardsRef.current;
    const connectors = document.querySelectorAll('.education-connector');

    // Reset initial states for cards (except the first card which should start visible)
    cards.forEach((card, index) => {
      if (index > 0) {
        const isRight = index % 2 === 0;
        gsap.set(card, { 
          x: isRight ? 80 : -80, 
          opacity: 0, 
          scale: 0.95 
        });
      }
    });

    // Animate connectors
    connectors.forEach((connector, index) => {
      const card = cards[index];
      const progressLine = connector.querySelector('.connector-progress');
      if (progressLine) {
        gsap.to(progressLine, {
          scrollTrigger: {
            trigger: card,
            start: 'bottom 85%',
            end: 'bottom 20%',
            scrub: 0.5,
            markers: false
          },
          height: '100%',
          ease: 'none'
        });
      }
    });

    // Animate card entries (cards index > 0)
    cards.forEach((card, index) => {
      if (index > 0) {
        const isRight = index % 2 === 0;
        gsap.to(card, {
          scrollTrigger: {
            trigger: card,
            start: 'top 90%',
            end: 'top 65%',
            scrub: 0.5,
            markers: false
          },
          x: 0,
          opacity: 1,
          scale: 1,
          ease: 'power1.out'
        });
      } else {
        // Animate first card in if page starts mid-scroll
        gsap.to(card, {
          scrollTrigger: {
            trigger: card,
            start: 'top 95%',
            end: 'top 80%',
            scrub: 0.5,
            markers: false
          },
          x: 0,
          opacity: 1,
          scale: 1
        });
      }
    });

    // Cleanup
    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, [isVisible]);

  const educationData = [
    {
      institution: 'Dayananda Sagar University',
      degree: 'B.Tech in Computer Science And Engineering',
      location: 'Bangalore, India',
      year: '2022 - 2026',
      achievements: [
        'Active participation in technical clubs',
        'Developed multiple real-world projects',
        'Patent granted for AI-based educational app'
      ],
      icon: '🎓'
    },
    {
      degree: '12th - Pre-University',
      institution: 'Narayana PU College',
      location: 'Bangalore, India',
      year: '2020 - 2022',
      achievements: [
        'Strong academic performance',
        'Developed interest in programming',
        'Participated in science exhibitions'
      ],
      icon: '📚'
    },
    {
      degree: '10th - Secondary Schooling',
      institution: 'Kendriya Vidyalaya - Hassan',
      location: 'Hassan, India',
      year: '2019 - 2020',
      achievements: [
        'Consistent academic performance',
        'Active in extracurricular activities',
        'Foundation in science and mathematics'
      ],
      icon: '🏫'
    }
  ];

  return (
    <div className={`education-section ${isVisible ? 'visible' : ''}`} ref={sectionRef}>
      <div className="container">
        <h2 className="section-title">
          <span className="title-number">02.</span>
          Education & Qualifications
        </h2>
        <div className="education-stack">
          {educationData.map((edu, index) => (
            <React.Fragment key={index}>
              <div 
                ref={el => cardsRef.current[index] = el}
                className={`education-card ${index % 2 === 0 ? 'card-right' : 'card-left'}`}
              >
                <div className="card-number">{String(index + 1).padStart(2, '0')}</div>
                <div className="education-icon">{edu.icon}</div>
                <div className="education-header">
                  <h3 className="education-degree">{edu.degree}</h3>
                  <div className="education-institution">
                    <span className="institution-name">{edu.institution}</span>
                    <span className="institution-location">{edu.location}</span>
                  </div>
                </div>
                <div className="education-meta">
                  <span className="education-year">{edu.year}</span>
                </div>
                <p className="education-description">{edu.description}</p>
              </div>
              {/* Connector between cards */}
              {index < educationData.length - 1 && (
                <div className="education-connector">
                  <div className="connector-line">
                    <div className="connector-progress"></div>
                  </div>
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Education;
